import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import './i18n'; // Initialize i18n
import { NavigationBar } from './NavigationBar';
import ProductView from './ProductView';
import RetailerView from './RetailerView';
import BrandDistributionView from './BrandDistributionView';
import RetailerDistributionView from './RetailerDistributionView';
import DistributionView from './DistributionView';
import ChatView from './ChatView';
import SimpleLogin from './SimpleLogin';
import CombinedLogin from './CombinedLogin';
import Auth0ProviderWrapper from './Auth0Provider';
import { AuthProvider, useAuth } from './AuthContext';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom'; // Import BrowserRouter, Route, Switch, Link
import Sidebar from './Sidebar'; // Make sure the path is correct
import PowerBIEmbed from "./PowerBIEmbed";
import SupportView from './SupportView';
import TicketDetailView from './TicketDetailView';
import TOCDashboard from './archived_components/TOCDashboard';
import TOCView from './TOCView';
import WorldMapView from './WorldMapView';
import IncidenceMapView from './IncidenceMapView';
         import BannerEvolutionView from './BannerEvolutionView';
         import JTIPriceAndPromotionsView from './JTIPriceAndPromotionsView';
         import ProductDetailView from './ProductDetailView';
         import ProductDetailViewUnified from './ProductDetailViewUnified';
         import DataSourcesOverview from './DataSourcesOverview';
         import ProductMultiCountryView from './ProductMultiCountryView';
         import { TOKMainView } from './toc-kpi';
         import UserSettings from './UserSettings';
         import AnugaDataView from './AnugaDataView';
         import BrandBoardView from './BrandBoardView';
         import BrandBoardSummaryView from './BrandBoardSummaryView';
import BrandBoardSummaryStyledView from './BrandBoardSummaryStyledView';
import BrandBoardSummaryPaletteView from './BrandBoardSummaryPaletteView';
import ProductCategorisationView from './ProductCategorisationView';
import PricingTypesView from './PricingTypesView';
         import Supervisor from './Supervisor';
         import RealEstateView from './RealEstateView';
         import RealEstateDetailView from './RealEstateDetailView';
         import RealEstateMapView from './RealEstateMapView';
import ExportPricingDataView from './ExportPricingDataView';
import JTIExportDataView from './JTIExportDataView';
import BatProductView from './BatProductView';
import BatProductMatchingView from './BatProductMatchingView';
import ReferenceCatalogView from './ReferenceCatalogView';
import BatEffectsView from './BatEffectsView';
import PriceEffectsGate from './PriceEffectsGate';
import EmbeddedAppView from './EmbeddedAppView';
import ProductMatchingView from './ProductMatchingView';
import ProductAvailabilityView from './ProductAvailabilityView';
import JTIProductsOverviewView from './JTIProductsOverviewView';
import RoleManagementView from './RoleManagementView';
import SwedenSnusAdminView from './SwedenSnusAdminView';
import SnusProductDetailView from './snus/SnusProductDetailView';
import SwedenSnusReportEmbedView from './snus/SwedenSnusReportEmbedView';
import SwedenSnusReportPublicView from './snus/SwedenSnusReportPublicView';
import { PRODUCT_VIEW_ROUTE_SEGMENT, DISTRIBUTION_VIEW_ROUTE_SEGMENT } from './productViewProjectConfig';
import ApiMaintenanceNotice from './ApiMaintenanceNotice';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
);

const getDefaultProjectPath = (projectKey) => {
  if (!projectKey) {
    return '/settings';
  }
  if (projectKey === 'bat') {
    return `/${projectKey}/export-pricing-data`;
  }
  if (projectKey === 'pmi') {
    return `/${projectKey}/${PRODUCT_VIEW_ROUTE_SEGMENT}/retailers`;
  }
  if (projectKey === 'asahi') {
    return `/${projectKey}/${PRODUCT_VIEW_ROUTE_SEGMENT}/retailers`;
  }
  if (projectKey === 'price_effects') {
    return `/${projectKey}/bat-effects`;
  }
  if (projectKey === 'jti') {
    return `/${projectKey}/jti-product/devices`;
  }
  if (projectKey === 'admin') {
    return `/${projectKey}/role-management`;
  }
  if (projectKey === 'matching') {
    return `/${projectKey}/bat-product-matching`;
  }
  if (projectKey === 'sweden-snus-admin') {
    return `/${projectKey}/data-insights`;
  }
  return `/${projectKey}/powerbi`;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, authType } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  const isJtiGlobalRoute = location.pathname === '/jti-global'
    || location.pathname.includes('/jti-global/');
  if (isJtiGlobalRoute && authType !== 'auth0-spa' && authType !== 'auth0-backend') {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ProjectKeyRoute = ({ allowedProjectKeys, children }) => {
  const { projectKey } = useAuth();
  const location = useLocation();

  if (allowedProjectKeys && !allowedProjectKeys.includes(projectKey)) {
    return <Navigate to={getDefaultProjectPath(projectKey)} state={{ from: location }} replace />;
  }

  return children;
};

const BatEffectsRoute = () => {
  const { projectKey } = useParams();
  if (projectKey !== 'price_effects') {
    return <Navigate to={getDefaultProjectPath(projectKey)} replace />;
  }
  return (
    <PriceEffectsGate>
      <BatEffectsView />
    </PriceEffectsGate>
  );
};

/** Redirect `/:projectKey/price-monitoring`, legacy `price-tracker` / `bat-product` → retailers subpage */
const PriceMonitoringRetailersRedirect = () => {
  const { projectKey } = useParams();
  return <Navigate to={`/${projectKey}/${PRODUCT_VIEW_ROUTE_SEGMENT}/retailers`} replace />;
};

/** Redirect `/:projectKey/distribution_view` → brands subpage */
const DistributionViewBrandsRedirect = () => {
  const { projectKey } = useParams();
  return <Navigate to={`/${projectKey}/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/brands`} replace />;
};

// Helper to extract project key from path
const getProjectKeyFromPath = (path) => {
  if (!path || path === '/' || path === '/settings') {
    return null;
  }
  if (path.startsWith('/supervisor') || path.startsWith('/data/') || path.startsWith('/report/')) {
    return null;
  }
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const [maybeProjectKey] = parts;
  if (['settings', 'support'].includes(maybeProjectKey)) {
    return null;
  }
  return maybeProjectKey || null;
};

// Login Route Component - redirects to dashboard if already logged in
const LoginRoute = () => {
  const { isAuthenticated, projectKey, username, isLoading } = useAuth();
  const location = useLocation();

  console.log('LoginRoute state:', { isAuthenticated, projectKey, username, isLoading, pathname: location.pathname });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading authentication...</div>
      </div>
    );
  }

  if (isAuthenticated && username) {
    // Check if we're already on a valid project route
    const pathProjectKey = getProjectKeyFromPath(location.pathname);
    
    // If we're on a project route that doesn't match state, trust the URL
    // This prevents redirecting when switching projects and state hasn't updated yet
    if (pathProjectKey && pathProjectKey !== projectKey) {
      console.log('LoginRoute: On project route', pathProjectKey, 'but state has', projectKey, '- staying on current route');
      return null; // Don't redirect, stay on current route
    }
    
    if (projectKey) {
      const targetPath = getDefaultProjectPath(projectKey);
      // Only redirect if we're not already on a route for this project
      if (!location.pathname.startsWith(`/${projectKey}/`) && location.pathname !== `/${projectKey}`) {
        console.log('LoginRoute: Redirecting to dashboard:', targetPath);
        return <Navigate to={targetPath} replace />;
      }
    } else if (!['/settings', '/support'].includes(location.pathname)) {
      // Only redirect to settings if we're not already there or on a project route
      if (!pathProjectKey) {
        return <Navigate to="/settings" replace />;
      }
    }
  }

  return <CombinedLogin />;
};

const JTI_GLOBAL_MARKET_OPTIONS = [
  { id: 'uk', label: 'United Kingdom' },
  { id: 'italy', label: 'Italy' },
  { id: 'romania', label: 'Romania' },
  { id: 'czech', label: 'Czech Republic' },
  { id: 'poland', label: 'Poland' },
];

const DEFAULT_JTI_GLOBAL_MARKET = JTI_GLOBAL_MARKET_OPTIONS[0].id;
const JTI_GLOBAL_SCOPE_KEY = 'jti-global-scope';
const JTI_GLOBAL_MARKET_KEY = 'jti-global-market';

const PROJECT_KEY_COOKIE_NAME = 'forloop_project_key';
const PROJECT_KEY_COOKIE_MAX_AGE_DAYS = 365;

const setProjectKeyCookie = (key) => {
  if (!key) return;
  const maxAge = PROJECT_KEY_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${PROJECT_KEY_COOKIE_NAME}=${encodeURIComponent(key)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const AppContent = () => {
  const { isAuthenticated, projectKey, username, isLoading, uiStyle } = useAuth();
  const location = useLocation();
  const pathProjectKey = getProjectKeyFromPath(location.pathname);
  const effectiveProjectKey = pathProjectKey === 'price_effects' ? 'price_effects' : (projectKey || pathProjectKey);
  const [scopeMode, setScopeMode] = useState(() => {
    try {
      const raw = localStorage.getItem(JTI_GLOBAL_SCOPE_KEY) || 'market';
      return raw === 'old' ? 'market' : raw;
    } catch (error) {
      return 'market';
    }
  });
  const [selectedMarket, setSelectedMarket] = useState(() => {
    try {
      return localStorage.getItem(JTI_GLOBAL_MARKET_KEY) || DEFAULT_JTI_GLOBAL_MARKET;
    } catch (error) {
      return DEFAULT_JTI_GLOBAL_MARKET;
    }
  });

  useEffect(() => {
    if (!['jti-global', 'jti'].includes(projectKey)) return;
    const validScopes = new Set(['hq', 'market']);
    if (!validScopes.has(scopeMode)) {
      setScopeMode('market');
    }
    if (!JTI_GLOBAL_MARKET_OPTIONS.some((option) => option.id === selectedMarket)) {
      setSelectedMarket(DEFAULT_JTI_GLOBAL_MARKET);
    }
  }, [projectKey, scopeMode, selectedMarket]);

  useEffect(() => {
    if (!['jti-global', 'jti'].includes(projectKey)) return;
    try {
      localStorage.setItem(JTI_GLOBAL_SCOPE_KEY, scopeMode);
      localStorage.setItem(JTI_GLOBAL_MARKET_KEY, selectedMarket);
    } catch (error) {
      console.warn('Unable to persist JTI global view preferences:', error);
    }
  }, [projectKey, scopeMode, selectedMarket]);

  // Persist project_key to cookie when user accesses a project route
  useEffect(() => {
    const pathProjectKey = getProjectKeyFromPath(location.pathname);
    if (pathProjectKey) {
      setProjectKeyCookie(pathProjectKey);
    }
  }, [location.pathname]);

  return (
    <Routes>
      {/* Standalone Anuga data page - no auth, no sidebar */}
      <Route path="/data/anuga" element={<AnugaDataView />} />
      {/* Published shelf report — no login, no sidebar. The hash is the access key. */}
      <Route path="/report/:hash/p/:productId" element={<SwedenSnusReportPublicView />} />
      <Route path="/report/:hash" element={<SwedenSnusReportPublicView />} />
      
      {/* Supervisor page - admin only, accessible only via URL, no sidebar */}
      {/* Must be before the catch-all route */}
      <Route 
        path="/supervisor" 
        element={<Supervisor />}
      />
      
      {/* Main app routes with auth and sidebar */}
      <Route path="*" element={
        <div>
          <div className="flex">
            {isAuthenticated && (
              <Sidebar
                username={username}
                projectKey={effectiveProjectKey}
                scopeMode={scopeMode}
                uiStyle={uiStyle}
              />
            )}
            <div style={{ flex: 1, marginLeft: isAuthenticated ? 300 : 0, paddingTop: '56px' }}>
              <NavigationBar
                isLoggedIn={isAuthenticated}
                projectKey={effectiveProjectKey}
                scopeMode={scopeMode}
                onScopeChange={setScopeMode}
                marketOptions={JTI_GLOBAL_MARKET_OPTIONS}
                selectedMarket={selectedMarket}
                onMarketChange={setSelectedMarket}
                uiStyle={uiStyle}
              />
              <Routes>
                {/* Login route - redirects to dashboard if logged in */}
                <Route path="/" element={<LoginRoute />} />
                
                {/* Protected routes - require authentication */}
                <Route path="/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
                <Route path="/support" element={<ProtectedRoute><SupportView /></ProtectedRoute>} />
                <Route path="/support/:ticketId" element={<ProtectedRoute><TicketDetailView /></ProtectedRoute>} />
                <Route path="/:projectKey/powerbi" element={<ProtectedRoute><PowerBIEmbed /></ProtectedRoute>} />
                <Route path="/:projectKey/product" element={<ProtectedRoute><ProductView /></ProtectedRoute>} />
                <Route path="/:projectKey/retailer" element={<ProtectedRoute><RetailerView /></ProtectedRoute>} />
                <Route path="/:projectKey/distribution_brand" element={<ProtectedRoute><BrandDistributionView /></ProtectedRoute>} />
                <Route path="/:projectKey/distribution_retailer" element={<ProtectedRoute><RetailerDistributionView /></ProtectedRoute>} />
                <Route
                  path={`/:projectKey/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/:groupBySegment`}
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['bat', 'pmi']}>
                        <DistributionView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`/:projectKey/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}`}
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['bat', 'pmi']}>
                        <DistributionViewBrandsRedirect />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/:projectKey/chat" element={<ProtectedRoute><ChatView /></ProtectedRoute>} />
                <Route path="/:projectKey/support" element={<ProtectedRoute><SupportView /></ProtectedRoute>} />
                <Route path="/:projectKey/support/:ticketId" element={<ProtectedRoute><TicketDetailView /></ProtectedRoute>} />
                <Route
                  path="/:projectKey/tocdashboard"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['jti-sweden']}>
                        <TOCDashboard />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/tocview"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['jti-sweden']}>
                        <TOCView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/toc-kpi/*"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['jti-sweden']}>
                        <TOKMainView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/:projectKey/worldmap" element={<ProtectedRoute><WorldMapView /></ProtectedRoute>} />
                <Route path="/:projectKey/worldmap-green" element={<ProtectedRoute><IncidenceMapView /></ProtectedRoute>} />
                <Route path="/:projectKey/banner-evolution" element={<ProtectedRoute><BannerEvolutionView /></ProtectedRoute>} />
                <Route
                  path="/:projectKey/jti-product"
                  element={
                    <ProtectedRoute>
                      <JTIPriceAndPromotionsView scopeMode={scopeMode} selectedMarket={selectedMarket} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/jti-product/:productType"
                  element={
                    <ProtectedRoute>
                      <JTIPriceAndPromotionsView scopeMode={scopeMode} selectedMarket={selectedMarket} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/:projectKey/product-detail/:countryCode/:productId" element={<ProtectedRoute><ProductDetailView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-detail/:productId" element={<ProtectedRoute><ProductDetailView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-detail" element={<ProtectedRoute><ProductDetailView /></ProtectedRoute>} />
                <Route path="/:projectKey/jti-products-overview" element={<ProtectedRoute><JTIProductsOverviewView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-detail-unified" element={<ProtectedRoute><ProductDetailViewUnified /></ProtectedRoute>} />
                <Route path="/:projectKey/product-matching" element={<ProtectedRoute><ProductMatchingView /></ProtectedRoute>} />
                <Route path="/:projectKey/jti-product-matching" element={<ProtectedRoute><ProductMatchingView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-availability" element={<ProtectedRoute><ProductAvailabilityView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-multi-country" element={<ProtectedRoute><ProductMultiCountryView /></ProtectedRoute>} />
                <Route path="/:projectKey/product-categorisation" element={<ProtectedRoute><ProductCategorisationView /></ProtectedRoute>} />
                <Route path="/:projectKey/pricing-types" element={<ProtectedRoute><PricingTypesView /></ProtectedRoute>} />
                <Route path="/:projectKey/export-pricing-data" element={<ProtectedRoute><ExportPricingDataView /></ProtectedRoute>} />
                <Route path="/:projectKey/jti-export-data" element={<ProtectedRoute><JTIExportDataView /></ProtectedRoute>} />
                <Route
                  path="/:projectKey/jti-embedded-app"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['jti-global', 'jti']}>
                        <EmbeddedAppView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`/:projectKey/${PRODUCT_VIEW_ROUTE_SEGMENT}/:groupBySegment`}
                  element={<ProtectedRoute><BatProductView /></ProtectedRoute>}
                />
                <Route
                  path={`/:projectKey/${PRODUCT_VIEW_ROUTE_SEGMENT}`}
                  element={<ProtectedRoute><PriceMonitoringRetailersRedirect /></ProtectedRoute>}
                />
                <Route
                  path="/:projectKey/price-tracker"
                  element={<ProtectedRoute><PriceMonitoringRetailersRedirect /></ProtectedRoute>}
                />
                <Route
                  path="/:projectKey/bat-product"
                  element={<ProtectedRoute><PriceMonitoringRetailersRedirect /></ProtectedRoute>}
                />
                <Route path="/price_effects" element={<ProtectedRoute><Navigate to="/price_effects/bat-effects" replace={true} /></ProtectedRoute>} />
                <Route path="/:projectKey/bat-effects" element={
                  <ProtectedRoute>
                    <BatEffectsRoute />
                  </ProtectedRoute>
                } />
                <Route path="/:projectKey/bat-product-matching" element={<ProtectedRoute><BatProductMatchingView /></ProtectedRoute>} />
                <Route path="/:projectKey/reference-catalog" element={<ProtectedRoute><ReferenceCatalogView /></ProtectedRoute>} />
                <Route path="/:projectKey/data-sources" element={<ProtectedRoute><DataSourcesOverview /></ProtectedRoute>} />
                <Route path="/:projectKey/brand-board" element={<ProtectedRoute><BrandBoardView /></ProtectedRoute>} />
                <Route path="/:projectKey/brand-board-summary" element={<ProtectedRoute><BrandBoardSummaryView /></ProtectedRoute>} />
                <Route path="/:projectKey/brand-board-summary-styled" element={<ProtectedRoute><BrandBoardSummaryStyledView /></ProtectedRoute>} />
                <Route
                  path="/:projectKey/brand-board-summary-palette"
                  element={
                    <ProtectedRoute>
                      <BrandBoardSummaryPaletteView scopeMode={scopeMode} selectedMarket={selectedMarket} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/brand-board-summary-palette/:productType"
                  element={
                    <ProtectedRoute>
                      <BrandBoardSummaryPaletteView scopeMode={scopeMode} selectedMarket={selectedMarket} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/:projectKey/real_estate" element={<ProtectedRoute><RealEstateView /></ProtectedRoute>} />
                <Route path="/:projectKey/real_estate/map" element={<ProtectedRoute><RealEstateMapView /></ProtectedRoute>} />
                <Route path="/:projectKey/real_estate/:id" element={<ProtectedRoute><RealEstateDetailView /></ProtectedRoute>} />
                <Route path="/:projectKey/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
                <Route
                  path="/:projectKey/role-management"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['admin']}>
                        <RoleManagementView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/data-insights"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['sweden-snus-admin']}>
                        <SwedenSnusAdminView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/shelf-reports"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['sweden-snus-admin']}>
                        <SwedenSnusReportEmbedView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:projectKey/snus-product/:skuKey"
                  element={
                    <ProtectedRoute>
                      <ProjectKeyRoute allowedProjectKeys={['sweden-snus-admin']}>
                        <SnusProductDetailView />
                      </ProjectKeyRoute>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </div>
        </div>
      } />
    </Routes>
  );
};

const App = () => {
  return (
    <Auth0ProviderWrapper>
      <Router>
        <AuthProvider>
          <AppContent />
          <ApiMaintenanceNotice />
        </AuthProvider>
      </Router>
    </Auth0ProviderWrapper>
  );
}


export default App;